import ReportAnalytics from "@/components/admin/ReportAnalytics";
import ReportComplianceDashboard from "@/components/admin/ReportComplianceDashboard";
import UserManagement from "@/components/admin/UserManagement";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import KPICard from "@/components/ui/kpi-card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { useEmailTemplate } from "@/hooks/use-email-template";
import { useReports } from "@/hooks/use-reports";
import { useUsers } from "@/hooks/use-users";
import {
  AlertCircle,
  BarChart3,
  Bell,
  Briefcase,
  CheckCircle2,
  Mail,
  Shield,
  Users,
} from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Reminders from "./Reminders";

export default function Admin() {
  const { reports, loading: reportsLoading } = useReports();
  const { users, loading: usersLoading } = useUsers();
  const { template, loading: templateLoading } = useEmailTemplate();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState("overview");

  // Calculate metrics
  const metrics = useMemo(() => {
    const uniqueClients = new Set(
      reports
        .filter((report) => report.clientName)
        .map((report) => report.clientName),
    );

    const totalUsers = users?.length || 0;
    const adminCount = users?.filter((u) => u.role === "admin").length || 0;
    const totalReports = reports?.length || 0;
    const clientCount = uniqueClients.size;
    const sentReports = reports?.filter((r) => r.sentByEmail).length || 0;
    const complianceRate = totalReports > 0 
      ? Math.round((sentReports / totalReports) * 100) 
      : 0;

    return {
      totalUsers,
      adminCount,
      totalReports,
      clientCount,
      complianceRate,
    };
  }, [reports, users]);

  // Normalize report task arrays
  const normalizedReports = reports.map((report) => {
    const normalizeTaskArray = (
      tasks: any[] | undefined,
    ): { value: string; dependency?: string }[] => {
      if (!tasks) return [];
      return tasks
        .map((task) =>
          typeof task === "string"
            ? { value: task, dependency: "" }
            : { value: task?.value || "", dependency: task?.dependency || "" },
        )
        .filter((task) => task.value?.trim());
    };
    return {
      ...report,
      last_week_tasks: normalizeTaskArray(report.last_week_tasks),
      Unplanned: normalizeTaskArray(report.Unplanned),
      blockers: normalizeTaskArray(report.blockers),
      next_week_tasks: normalizeTaskArray(report.next_week_tasks),
      further_beyond: normalizeTaskArray(report.further_beyond),
    };
  });

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="page-header">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">
              Admin Dashboard
            </h1>
            <Badge variant="default" className="gap-1">
              <Shield className="h-3 w-3" />
              Admin
            </Badge>
          </div>
          <p className="text-muted-foreground mt-1">
            Manage users, monitor compliance, and configure system settings
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground hidden md:block">
            {user.email}
          </span>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
        <KPICard
          title="Total Users"
          value={metrics.totalUsers}
          subtitle={`${metrics.adminCount} admins`}
          icon={Users}
        />
        <KPICard
          title="Total Reports"
          value={metrics.totalReports}
          subtitle="All time"
          icon={BarChart3}
        />
        <KPICard
          title="Active Clients"
          value={metrics.clientCount}
          subtitle="Unique clients"
          icon={Briefcase}
        />
        <KPICard
          title="Compliance Rate"
          value={`${metrics.complianceRate}%`}
          subtitle="Reports sent"
          icon={CheckCircle2}
        />
        <KPICard
          title="Email Template"
          value={template ? "Active" : "None"}
          subtitle="Current template"
          icon={Mail}
        />
      </div>

      {/* System Status Banner */}
      <Card className="bg-success/5 border-success/20">
        <CardContent className="py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
              <CheckCircle2 className="h-5 w-5 text-success" />
            </div>
            <div>
              <p className="font-medium text-foreground">
                All Systems Operational
              </p>
              <p className="text-sm text-muted-foreground">
                Backend services, email delivery, and storage are functioning
                normally
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Main Tabs */}
      <Tabs
        value={activeTab}
        onValueChange={setActiveTab}
        className="space-y-6"
      >
        <TabsList className="w-full sm:w-auto flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="overview" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            <span className="hidden sm:inline">Overview</span>
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2">
            <Users className="h-4 w-4" />
            <span className="hidden sm:inline">Users</span>
          </TabsTrigger>
          <TabsTrigger value="compliance" className="gap-2">
            <AlertCircle className="h-4 w-4" />
            <span className="hidden sm:inline">Compliance</span>
          </TabsTrigger>
          <TabsTrigger value="reminders" className="gap-2">
            <Bell className="h-4 w-4" />
            <span className="hidden sm:inline">Reminders</span>
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-0">
          <ReportAnalytics
            reports={normalizedReports}
            loading={reportsLoading}
          />
        </TabsContent>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-6 mt-0">
          <UserManagement />
        </TabsContent>

        {/* Compliance Tab */}
        <TabsContent value="compliance" className="space-y-6 mt-0">
          <ReportComplianceDashboard
            reports={reports}
            users={users}
            loading={reportsLoading}
          />
        </TabsContent>

        {/* Reminders Tab */}
        <TabsContent value="reminders" className="mt-0">
          <Reminders />
        </TabsContent>
      </Tabs>
    </div>
  );
}
